import { HttpClient, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { EnvironmentProviders, importProvidersFrom, inject, makeEnvironmentProviders } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'; //TO DO: replace by CSS + animate.enter/leave strategy for anmiations
import { provideRouter, RedirectCommand, Router, Routes, withComponentInputBinding, withNavigationErrorHandler } from '@angular/router';
import { FormlyModule } from '@ngx-formly/core';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { NgWizardConfig, NgWizardModule, THEME } from 'ng-wizard';

import { AuthService, IDENTITY_API_URL_TOKEN, IDENTITY_MENU_PARAMS, IDENTITY_ROOT_MODULE, IdentityRootModule } from '@auth';
import { SasDynamicFormsTypes } from '@components';
import { environment } from '@environments';
import { authInterceptor, errorInterceptor, httpInterceptorProviders, noopInterceptorProvider, spinnerInterceptor, translationInterceptor } from '@interceptors';
import { BREADCRUMB_CONFIG } from '@layout';
import { MessageService, TOAST_CONFIG } from '@messages';
import { SearchDataService } from '@services';
import { HttpLoaderFactory, MyMissingTranslationHandler } from '@translations';
import { APP_VERSION_TOKEN } from './app-version.token';
import { provideConfigInitializer } from './config-initializer';

export type AgileAppOptions = {
    routes: Routes;
    /** Defaults to the `app-version` meta tag written by set-version.js. */
    version?: string;
    /** Extra query params for GET /api/menu (e.g. { menuProduct: 'APASONE' }). Omit for apps that send none. */
    menuParams?: Record<string, string>;
    /** For apps that serve one module at the client root: no module URL segment and no landpage. */
    rootModule?: IdentityRootModule;
};

function readVersionFromMeta(): string {
    return document.querySelector('meta[name="app-version"]')?.getAttribute('content') ?? '';
}

const ngWizardConfig: NgWizardConfig = {
    theme: THEME.dots,
};

export function provideAgileApp({ routes, version, menuParams, rootModule }: AgileAppOptions): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideConfigInitializer(routes),
        importProvidersFrom(
            FormlyModule.forRoot({ types: SasDynamicFormsTypes }),
            NgWizardModule.forRoot(ngWizardConfig)
        ),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([spinnerInterceptor, authInterceptor, translationInterceptor, errorInterceptor])
        ),
        noopInterceptorProvider,
        httpInterceptorProviders,
        { provide: IDENTITY_API_URL_TOKEN, useValue: environment.identityAPI },
        { provide: APP_VERSION_TOKEN, useValue: version ?? readVersionFromMeta() },
        { provide: IDENTITY_MENU_PARAMS, useValue: menuParams ?? {} },
        { provide: IDENTITY_ROOT_MODULE, useValue: rootModule ?? null },
        { provide: BREADCRUMB_CONFIG, useValue: { excludedRoutes: ['signIn', 'signup', 'reset-password', 'forgot-password', 'authorize'] } },
        MessageService,
        AuthService,
        provideRouter([], withComponentInputBinding(), withNavigationErrorHandler((error: any) => {
            console.error('Navigation error:', error);
            const router = inject(Router);
            const urlTree = router.createUrlTree(['/error']);
            return new RedirectCommand(urlTree);
        })),
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient]
                },
                missingTranslationHandler: {
                    provide: MissingTranslationHandler,
                    useClass: MyMissingTranslationHandler
                }
            })
        ),
        provideHotToastConfig(TOAST_CONFIG),
        provideAnimationsAsync(),
        SearchDataService
    ]);
}
